// Taken from:
// https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
import * as validators from '@vuelidate/validators'
// import { i18n } from "@/i18n"
import { unref, type Ref } from 'vue'

import i18n from '../plugins/i18n'

// or import { createI18nMessage } from '@vuelidate/validators'
const { createI18nMessage } = validators

export const minLengthIfValidator = (length: number, condition: boolean | Ref<boolean>) =>
  validators.helpers.withParams({ type: 'minLengthIf', min: length }, (value?: string) => {
    if (!unref(condition)) return true
    return (value?.toString() ?? '').length >= length
  })

export const maxLengthIfValidator = (length: number, condition: boolean | Ref<boolean>) =>
  validators.helpers.withParams({ type: 'maxLengthIf', max: length }, (value?: string) => {
    if (!unref(condition)) return true
    return (value?.toString() ?? '').length <= length
  })

export const containsValidator = (str: string) =>
  validators.helpers.withParams(
    { type: 'contains', value: str },
    (value?: string) => !validators.helpers.req(value) || (!!value && value.includes(str)),
  )

export const dateAfterValidator = (date: string | null | undefined) =>
  validators.helpers.withParams({ type: 'dateAfter', value: date }, (value?: string) => {
    if (!value || !date) return true
    return new Date(value) > new Date(date)
  })

export const dateAfterOrEqualValidator = (date: string | null | undefined) =>
  validators.helpers.withParams({ type: 'dateAfterOrEqual', value: date }, (value?: string) => {
    if (!value || !date) return true
    return new Date(value) >= new Date(date)
  })

export const uniqueArrayElementValidator = (elementsOrFunction: string[] | (() => string[]), skipIf?: boolean) =>
  validators.helpers.withParams({ type: 'uniqueArrayElement', prop: elementsOrFunction, skip: skipIf }, (value?: string) => {
    if (skipIf) {
      return true
    }
    let elements
    if (elementsOrFunction instanceof Array) {
      elements = elementsOrFunction
    } else {
      elements = elementsOrFunction()
    }

    return value ? !elements.includes(value) : true
  })

// Create your i18n message instance. Used for vue-i18n@9
export const withI18nMessage = createI18nMessage({ t: i18n.global.t.bind(i18n) })
// for vue-i18n@8
// const withI18nMessage = createI18nMessage({ t: i18n.t.bind(i18n) } as any)

// wrap each validator.
export const required = withI18nMessage(validators.required)
export const email = withI18nMessage(validators.email)
export const numeric = withI18nMessage(validators.numeric)
export const url = withI18nMessage(validators.url)

// validators that expect a parameter should have `{ withArguments: true }` passed as a second parameter, to annotate they should be wrapped
export const minLength = withI18nMessage(validators.minLength, { withArguments: true })
export const minLengthIf = withI18nMessage(minLengthIfValidator, { withArguments: true })
export const maxLength = withI18nMessage(validators.maxLength, { withArguments: true })
export const maxLengthIf = withI18nMessage(maxLengthIfValidator, { withArguments: true })
export const requiredIf = withI18nMessage(validators.requiredIf, { withArguments: true })
export const requiredUnless = withI18nMessage(validators.requiredUnless, { withArguments: true })
export const minValue = withI18nMessage(validators.minValue, { withArguments: true })
export const maxValue = withI18nMessage(validators.maxValue, { withArguments: true })

export const contains = withI18nMessage(containsValidator, { withArguments: true })
export const uniqueArrayElement = withI18nMessage(uniqueArrayElementValidator, { withArguments: true })
export const dateAfter = withI18nMessage(dateAfterValidator, { withArguments: true })
export const dateAfterOrEqual = withI18nMessage(dateAfterOrEqualValidator, { withArguments: true })
